/**
 * The institutional dashboard. This is the product being sold.
 *
 * ── ONE FILTER OBJECT, AND IT LIVES IN THE URL ───────────────────────────
 * Every control writes to search params and every panel reads the same
 * derived object. That is not a preference: it makes the state shareable by
 * pasting a link, makes the back button work, makes a saved view a stored
 * string rather than a serialized component tree, and makes the export inherit
 * exactly what the reader is looking at. The panel this replaces had four
 * uncontrolled selects sitting above a card claiming reports could be built on
 * any filter combination.
 *
 * A control that cannot be wired to a real field does not ship. There is no
 * region filter, no major filter, no class-year filter and no engagement tier
 * here, because none of those fields exists on a P3 student record. Data and
 * methods names each one and says so.
 *
 * ── THE PAGE'S SHAPE IS THE MEMBER APP'S /admin ──────────────────────────
 * Header with the sample pill and Export PDF, the maroon hero band with four
 * border-separated figures, a filter bar card, then the sections. Measured
 * against platform.pulseofp3.org/admin on Sep 9 2026 rather than read from
 * its source, because two earlier surfaces matched the wrong file. The one
 * departure is that the hero's figures carry a denominator line where the
 * member app carries a delta pill: every tenant here is in its first term,
 * and there is no prior period to be up against. See lib/hero.ts.
 *
 * ── THREE NAVIGATION MOVES, CHOSEN FOR THIS READER ───────────────────────
 *   The command palette (⌘K) is scoped to what a menu cannot do: change the
 *   window, toggle the comparison, jump to a section by the QUESTION it
 *   answers, copy the join link, run the export. A palette that mirrors the
 *   nav is decoration.
 *
 *   Comparison mode offers only comparisons P3 computes both sides of. There
 *   is no peer-institution option, because P3 has no peer set, and inventing
 *   one is what the panel this replaces did five times over. The previous-term
 *   option is present and explicitly unavailable in a first term, with the
 *   reason, rather than silently absent or silently empty.
 *
 *   Deep links carry the whole state, including which demonstration cohort is
 *   loaded, so a link to "students waiting for a match, last 90 days" opens on
 *   exactly that.
 *
 * ── THE EXPORT AND THE PAGE CANNOT DISAGREE ──────────────────────────────
 * The CSV in lib/csv.ts is generated from the same derived object the cards
 * render, with the same suppression already applied, so there is no second
 * code path that could drift. It contains aggregates only.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  AdminShell,
  ExportPdfButton,
  type PaletteCommand,
} from "~/app/components/AdminShell";
import { viewerOf, type SessionPayload } from "~/app/lib/session";
import { LiveGate, useLiveTenant } from "~/app/components/LiveTenant";
import {
  Button,
  CellValue,
  Chip,
  Select,
  Trace,
} from "~/app/components/primitives";
import { ADMIN_SECTIONS, type AdminSectionId } from "~/app/lib/admin-nav";
import { getCohorts, getMetrics, type CohortRecord, type MetricsPayload } from "~/app/lib/api";
import { cn } from "~/app/lib/cn";
import { downloadCsv } from "~/app/lib/csv";
import { liveView } from "~/app/lib/live-view";
import {
  COMPARISONS,
  DEFAULT_FILTERS,
  DEFAULT_SCENARIO,
  DEMO_COHORTS,
  MENTORSHIP_FILTERS,
  SCENARIOS,
  activeFilterChips,
  cohortIdFromSearch,
  demoCohortFromSearch,
  deriveCohort,
  filtersAreDefault,
  scenarioFromSearch,
  type CohortFilters,
  type CohortView,
  type ComparisonId,
  type MentorshipFilterId,
  type ScenarioId,
  type WindowId,
} from "~/app/lib/demo-data";
import { formatCount } from "~/app/lib/format";
import { heroKpis, splitAccent } from "~/app/lib/hero";
import { ActivityPanel } from "~/app/panels/ActivityPanel";
import { CohortBuilding } from "~/app/panels/CohortBuilding";
import { CutUnavailable, type RefusedCut, refusedHeroLine, refusedRailReason } from "~/app/panels/CutUnavailable";
import { MentorshipPanel } from "~/app/panels/MentorshipPanel";
import { MilestonesPanel } from "~/app/panels/MilestonesPanel";
import { NeedsPanel } from "~/app/panels/NeedsPanel";
import { OverviewPanel } from "~/app/panels/OverviewPanel";

/* ==========================================================================
   URL state

   Read on mount and on every popstate, written with replaceState for a filter
   change and pushState for a navigation-shaped change, so the back button
   walks the reader's own path rather than every keystroke.
   ========================================================================== */

interface PageState {
  readonly scenario: ScenarioId;
  readonly filters: CohortFilters;
  /** One of the institution's own cohorts (Sep 17 2026), from `?cohort=`: a
   *  `coh_…` id on the signed-in host, one of DEMO_COHORTS on the sample. The
   *  sample's scenario has its own parameter, `scenario`. */
  readonly cohortId: string | null;
}

/** Said on the selector, as the brief asked: the floor applies to a cohort alone. */
export const COHORT_SELECTOR_NOTE = "Figures for a cohort appear at 10 students.";

/** The Worker's own sentence for a slice not computed yet (routes/admin.ts,
 *  SNAPSHOT_PENDING), which the client reads as an empty document. */
const SLICE_PENDING = "These figures are prepared on the next hourly recomputation, or now with Recompute.";

function readState(demo: boolean): PageState {
  if (typeof window === "undefined") {
    return { scenario: DEFAULT_SCENARIO, filters: DEFAULT_FILTERS, cohortId: null };
  }
  const params = new URLSearchParams(window.location.search);
  // Each host reads only the ids it can serve: a sample id is never fetched
  // from the Worker, and a real one is never looked for in the fixtures.
  const cohortId = demo ? demoCohortFromSearch(window.location.search) : cohortIdFromSearch(window.location.search);

  const scenario = scenarioFromSearch(window.location.search);

  // The sample computes at its default window and no longer reads `?window=`:
  // nothing on the page can set it any more, and a figure must never move by
  // a parameter the reader cannot see.
  const windowId: WindowId = DEFAULT_FILTERS.window;

  const mentorshipParam = params.get("mentorship") ?? "";
  const mentorship: MentorshipFilterId = MENTORSHIP_FILTERS.some(
    (m) => m.id === mentorshipParam,
  )
    ? (mentorshipParam as MentorshipFilterId)
    : DEFAULT_FILTERS.mentorship;

  const compareParam = params.get("compare") ?? "";
  const compare: ComparisonId = COMPARISONS.some((c) => c.id === compareParam)
    ? (compareParam as ComparisonId)
    : DEFAULT_FILTERS.compare;

  // A link that carries both a cohort and a mentorship cut reads as the
  // cohort alone: the two are never crossed.
  return { scenario, filters: { window: windowId, mentorship: cohortId ? "any" : mentorship, compare, cohort: cohortId }, cohortId };
}

function writeState(state: PageState, push: boolean): void {
  if (typeof window === "undefined") return;
  const params = new URLSearchParams();
  if (state.scenario !== DEFAULT_SCENARIO) params.set("scenario", state.scenario);
  if (state.cohortId) params.set("cohort", state.cohortId);
  if (state.filters.mentorship !== DEFAULT_FILTERS.mentorship) {
    params.set("mentorship", state.filters.mentorship);
  }
  if (state.filters.compare !== DEFAULT_FILTERS.compare) {
    params.set("compare", state.filters.compare);
  }
  const query = params.toString();
  const url = `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`;
  if (push) window.history.pushState(null, "", url);
  else window.history.replaceState(null, "", url);
}

/* ==========================================================================
   The hero band

   The member app's gradient band, measured: 18px radius, 2rem by 2.25rem
   padding, a 29px heading at 1.5 line-height with one accent word, a 14.5px
   sub at 820px, then four figures separated by hairlines. Every figure is a
   Cell rendered through CellValue and wrapped in a Trace, so a suppressed
   headline reads n<10 with its reason on hover, the same as anywhere else.
   ========================================================================== */

function Hero({ view, cohortLabel, refused = null }: { view: CohortView; cohortLabel?: string; refused?: RefusedCut | null }) {
  const { head, accent } = splitAccent(view.institution.legalName);
  const kpis = heroKpis(view);

  return (
    /* is-building (Sep 18 2026): on day one the four headline figures are
       real zeros, and drawn at full weight they made a paying customer's
       first screen a wall of 0s. They step back to the withheld tone until
       the floor is crossed; the building card below carries the page. */
    <section className={cn("e-hero", view.belowFloor && "is-building")} aria-labelledby="e-hero-title">
      <h1 className="e-hero-h1" id="e-hero-title">
        {head}
        <span className="e-hero-accent">{accent}</span>
      </h1>
      <p className="e-hero-sub">
        {refused
          ? refusedHeroLine(refused)
          : cohortLabel
          ? view.belowFloor
            ? `${cohortLabel}: figures appear once ten of your students are in it.`
            : `${cohortLabel}: the students your team placed in it, what they do, and how many have a mentor.`
          : view.belowFloor
            ? "Your students, once ten have claimed you. Until then this page is a claiming tool."
            : view.activity.recordsMeasured
              ? "Your students on P3: who has claimed you, what they do, and how many have a mentor."
              : "Your students on P3: who has claimed you, what they asked for, and how many have a mentor."}
      </p>
      {/* A refused cut (Sep 19 2026) has no document, so no stamp, no held
          or stale line and no figures: four "Not available" tiles and a
          "0% of your band" said nothing true. Its card carries the page. */}
      <p className="e-hero-asof e-asof">
        {refused ? view.institution.term.name : `${view.institution.term.name} · ${view.freshnessLine}`}
      </p>
      {refused ? null : view.held ? (
        // Held (Sep 19 2026, the Worker's lib/cut-guard.ts): the date above
        // is the held document's own, and the next computation does NOT
        // replace it, so the stale sentence would be false here.
        <p className="e-hero-asof e-asof">
          These figures are held so no student can be singled out. They update
          once enough students have changed.
        </p>
      ) : view.stale ? (
        <p className="e-hero-asof e-asof">
          These figures are more than three hours old. The next computation
          replaces them.
        </p>
      ) : null}
      {refused ? null : (
      <div className="e-hero-kpis">
        {kpis.map((kpi) => (
          <div className="e-hero-kpi" key={kpi.label}>
            <p className="e-hero-kpi-label">{kpi.label}</p>
            <p className="e-hero-kpi-value">
              <Trace provenance={kpi.provenance} label={kpi.label}>
                <CellValue cell={kpi.cell} format={formatCount} label={kpi.label} />
              </Trace>
            </p>
            <p className="e-hero-kpi-note">{kpi.note}</p>
          </div>
        ))}
      </div>
      )}
    </section>
  );
}

/* ==========================================================================
   The filter bar

   The member app's filter card: a small FILTERS label, the selects in a row,
   the active chips, and the refresh stamp on the right. Every select is wired
   to a named field; the first one is the demonstration cohort, which exists
   because three states of this product are real screens a customer will see,
   and shipping only the populated one is how a dashboard ends up unusable for
   its first six weeks.
   ========================================================================== */

function FilterBar({
  state,
  view,
  live,
  cohorts,
  cohortNote,
  update,
  setFilters,
}: {
  state: PageState;
  view: CohortView;
  /** A signed-in tenant (Sep 10 2026). The snapshot is one whole-population
   *  read with no window and no per-student filter, so the bar carries the
   *  two controls that apply to it: the cohort (Sep 17 2026) and the
   *  comparison. The sample scenario, the window and the student filter are
   *  the sample's. */
  live: boolean;
  /** The institution's active cohorts (the sample's own on /demo), or null
   *  while unknown or for a viewer. */
  cohorts: readonly { readonly id: string; readonly name: string }[] | null;
  /** What the bar says beside the cohort select: the floor, a load in progress, or a failure. */
  cohortNote: string | null;
  update: (next: Partial<PageState> | ((s: PageState) => PageState), push?: boolean) => void;
  setFilters: (patch: Partial<CohortFilters>) => void;
}) {
  const chips = live ? [] : activeFilterChips(view.filters);
  const prevAvailable = view.comparisonAvailable.prev.ok;
  const scenario = SCENARIOS.find((s) => s.id === state.scenario);
  // On a phone the bar folds (Sep 18 2026): four full-width selects were
  // about 200px of controls before any figure. Below 600px only this button
  // shows until it is pressed; above, it is not rendered and the bar is the
  // bar. The label says how many filters are off their default, so a folded
  // bar never hides that a reading is filtered.
  const [foldOpen, setFoldOpen] = useState(false);
  const activeCount =
    (view.filters.cohort ? 1 : 0) +
    (view.filters.compare !== DEFAULT_FILTERS.compare ? 1 : 0) +
    (view.filters.mentorship !== DEFAULT_FILTERS.mentorship ? 1 : 0);
  const fold = (
    <button
      type="button"
      className="e-filterbar-fold"
      aria-expanded={foldOpen}
      aria-controls="e-filterbar-body"
      onClick={() => setFoldOpen((o) => !o)}
    >
      <span className="e-filterbar-label">Filters{activeCount > 0 ? ` · ${activeCount} on` : ""}</span>
      <span className="e-filterbar-fold-glyph" aria-hidden="true">{foldOpen ? "\u25BE" : "\u25B8"}</span>
    </button>
  );

  const compare = (
    <Select
      aria-label="Compare with"
      value={view.filters.compare}
      onChange={(e) => setFilters({ compare: e.target.value as ComparisonId })}
    >
      {COMPARISONS.map((c) => (
        <option
          key={c.id}
          value={c.id}
          disabled={c.id === "prev" && !prevAvailable}
        >
          {c.id === "none" ? "No comparison" : `Compare with ${c.label.toLowerCase()}`}
          {c.id === "prev" && !prevAvailable ? " (not available yet)" : ""}
        </option>
      ))}
    </Select>
  );

  // The institution's own cohorts, on both hosts (Sep 17 2026, Thomas). The
  // selection travels in the URL and, on the signed-in host, fetches the
  // cohort's own document; on the sample it scopes the fixtures.
  // Matched / Waiting, live since Sep 17 2026: the tick writes a document for
  // each cut, so the floor applies to the cut itself. It is off while a cohort
  // is chosen, because the two are not crossed: that would be a document per
  // cohort per slice on every tick for a reading nobody has asked for.
  const cohortChosen = view.filters.cohort !== null && view.filters.cohort !== undefined;
  const students = (
    <Select
      aria-label="Students"
      title={cohortChosen ? "Choose All cohorts to filter by whether a student has a mentor." : undefined}
      value={view.filters.mentorship}
      disabled={cohortChosen}
      onChange={(e) => setFilters({ mentorship: e.target.value as MentorshipFilterId })}
    >
      {MENTORSHIP_FILTERS.map((m) => (
        <option key={m.id} value={m.id}>
          {m.label}
        </option>
      ))}
    </Select>
  );

  const cohortSelect =
    cohorts && cohorts.length > 0 ? (
      <Select
        aria-label="Cohort"
        title={COHORT_SELECTOR_NOTE}
        value={state.cohortId ?? ""}
        onChange={(e) => {
          const cohortId = e.target.value || null;
          update((current) => ({ ...current, cohortId, filters: { ...current.filters, cohort: cohortId } }), true);
        }}
      >
        <option value="">Whole institution</option>
        {cohorts.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </Select>
    ) : null;

  if (live) {
    // One label for the row, not one per control (Thomas, Sep 17 2026): the
    // sample reads "Filters" and then its selects, and a signed-in reader who
    // has seen the sample should meet the same row. Each select keeps its own
    // aria-label, which is what a screen reader reads; the visible labels were
    // the only thing that differed.
    return (
      <section className={cn("e-filterbar no-print", foldOpen && "is-open")} aria-label="Filters">
        {fold}
        <div className="e-filterbar-body" id="e-filterbar-body">
          <span className="e-filterbar-label">Filters</span>
          {cohortSelect}
          {compare}
          {students}
          {cohortNote ? <span className="e-filterbar-note">{cohortNote}</span> : null}
        </div>
      </section>
    );
  }

  return (
    <section className={cn("e-filterbar no-print", foldOpen && "is-open")} aria-label="Filters">
      {fold}
      <div className="e-filterbar-body" id="e-filterbar-body">
      <span className="e-filterbar-label">Filters</span>

      <Select
        aria-label="Sample scenario"
        title={scenario ? `Sample data. ${scenario.blurb}` : undefined}
        value={state.scenario}
        onChange={(e) => update({ scenario: e.target.value as ScenarioId }, true)}
      >
        {SCENARIOS.map((s) => (
          <option key={s.id} value={s.id}>
            {s.label} · {s.caption.replace(" students claimed", " claimed")}
          </option>
        ))}
      </Select>

      {/* The window select left the sample on Sep 17 2026 (Thomas). A real
          tenant has no such control and will not get one: each live figure
          carries its OWN window in its provenance, because app use is read
          over one period and a student's milestones span their whole record,
          so one dashboard-wide window would be a claim the data cannot keep.
          A control a prospect sees here and never gets is a promise broken on
          the first day. The sample still computes at its default term, which
          is what every figure's provenance says. */}

      {cohortSelect}

      {compare}

      {students}

      {chips.map((chip) => (
        <Chip
          key={chip.key}
          tone="accent"
          onRemove={() => update((current) => ({ ...current, filters: chip.clearTo }))}
          removeLabel={`Remove the filter ${chip.label}`}
        >
          {chip.label}
        </Chip>
      ))}

      {chips.length > 0 ? (
        <span className="e-filterbar-note">
          {view.claimed.kind === "value"
            ? `${formatCount(view.claimed.value)} of ${formatCount(view.cohortSize)} students match`
            : "Fewer than ten students match, so figures for this selection are not published"}
        </span>
      ) : cohortNote ? (
        <span className="e-filterbar-note">{cohortNote}</span>
      ) : null}

      {!filtersAreDefault(view.filters) ? (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            update((current) => ({
              ...current,
              filters: { ...DEFAULT_FILTERS, compare: current.filters.compare },
            }))
          }
        >
          Clear filters
        </Button>
      ) : null}
      </div>
    </section>
  );
}

/* ==========================================================================
   The page
   ========================================================================== */

/**
 * `demo` is the signed-out shop window at /demo: the same dashboard on the
 * same fixtures, with a banner that says so and a way back to the pricing
 * page. It exists so the team and a prospect can be sent a link without an
 * account, which is the whole point of Thomas's ask.
 *
 * It is a PROP rather than a separate page because the day this dashboard
 * grows a real session guard, /demo must keep rendering and /admin must not.
 * Splitting them into two components would guarantee they drift.
 */
export function AdminDashboard({
  demo = false,
  session,
}: {
  demo?: boolean;
  /** The signed-in person, on /admin (Sep 10 2026): the institution, the
   *  plan, the claim campaign and the figures come from the Worker for
   *  their session, through `liveView`. Without one the page renders the
   *  sample fixtures, which is what /demo is. */
  session?: SessionPayload;
} = {}) {
  const [state, setState] = useState<PageState>(() => readState(demo));
  const live = useLiveTenant(session);

  useEffect(() => {
    const onPop = () => setState(readState(demo));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [demo]);

  const update = useCallback(
    (next: Partial<PageState> | ((s: PageState) => PageState), push = false) => {
      setState((current) => {
        const resolved =
          typeof next === "function" ? next(current) : { ...current, ...next };
        writeState(resolved, push);
        return resolved;
      });
    },
    [],
  );

  const setFilters = useCallback(
    (patch: Partial<CohortFilters>) => {
      update((current) => {
        const filters = { ...current.filters, ...patch };
        // A cohort turns the mentorship filter off, and to "All students", so
        // the disabled select never shows a cut that is not applied.
        return { ...current, filters: filters.cohort ? { ...filters, mentorship: "any" } : filters };
      });
    },
    [update],
  );

  // The institution's own cohorts (Sep 17 2026), for the selector. Admin
  // and owner only: a viewer's 403 leaves the bar as it was.
  const [cohortList, setCohortList] = useState<readonly CohortRecord[] | null>(null);
  useEffect(() => {
    if (!session || session.role === "viewer") return;
    let cancelled = false;
    void getCohorts().then((result) => {
      if (cancelled) return;
      setCohortList(result.ok ? result.value.cohorts.filter((c) => c.archivedAt === null) : []);
    });
    return () => {
      cancelled = true;
    };
  }, [session]);

  // The document for whatever cut is selected, read when the selection
  // changes. A cut is a cohort of the institution's own or one of the two
  // slices (Sep 17 2026), never both: the filter bar offers only one of them
  // at a time and the Worker refuses the pair.
  //
  // A pending one (no tick since the cohort was made, or since this Worker
  // learned to write slices) arrives as null and renders the building state; a
  // cohort that is no longer the institution's sends the page back to the
  // whole institution.
  const selection: { readonly key: string; readonly cut: { cohort?: string; slice?: "matched" | "waiting" } } | null =
    state.cohortId !== null
      ? { key: `cohort:${state.cohortId}`, cut: { cohort: state.cohortId } }
      : state.filters.mentorship !== "any"
        ? { key: `slice:${state.filters.mentorship}`, cut: { slice: state.filters.mentorship } }
        : null;
  type CutMetrics =
    | { readonly kind: "idle" }
    | { readonly kind: "loading"; readonly key: string }
    | { readonly kind: "ready"; readonly key: string; readonly metrics: MetricsPayload | null }
    | { readonly kind: "down"; readonly key: string; readonly message: string; readonly code: string };
  const [cohortMetrics, setCohortMetrics] = useState<CutMetrics>({ kind: "idle" });
  const selectionKey = selection?.key ?? null;
  const selectionCut = selection?.cut;
  useEffect(() => {
    if (!session || !selectionKey || !selectionCut) {
      setCohortMetrics({ kind: "idle" });
      return;
    }
    let cancelled = false;
    setCohortMetrics({ kind: "loading", key: selectionKey });
    void getMetrics(selectionCut).then((result) => {
      if (cancelled) return;
      if (result.ok) setCohortMetrics({ kind: "ready", key: selectionKey, metrics: result.value });
      else if (result.status === 404) {
        update({ cohortId: null }, true);
        setCohortMetrics({ kind: "idle" });
      } else setCohortMetrics({ kind: "down", key: selectionKey, message: result.error.message, code: result.error.code });
    });
    return () => {
      cancelled = true;
    };
    // selectionCut is derived from selectionKey; keying on the string keeps
    // this from refetching on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, selectionKey, update]);

  const cohortReady =
    selectionKey !== null && cohortMetrics.kind === "ready" && cohortMetrics.key === selectionKey ? cohortMetrics : null;
  // A cut that could not be served (held back by the guard, or any other
  // refusal) is drawn with no figures at all, never the institution's figures
  // under the cut's name; `refusedCut` below says which state it is in.
  const cutDown =
    selectionKey !== null && cohortMetrics.kind === "down" && cohortMetrics.key === selectionKey;

  const view = useMemo(
    () =>
      live.state.kind === "ready"
        ? liveView(
            cohortReady
              ? { ...live.state.data, metrics: cohortReady.metrics }
              : cutDown
                ? { ...live.state.data, metrics: null }
                : live.state.data,
            state.filters,
          )
        : deriveCohort(state.scenario, { ...state.filters, cohort: state.cohortId }),
    [live.state, state.scenario, state.filters, state.cohortId, cohortReady, cutDown],
  );

  // The seat meter is the institution's whatever cut is on screen (Sep 19
  // 2026): seats are bought for the institution, and a refused cut, having no
  // document, read "Not shown of 300" and "0% seats used" in the rail.
  const wholeSeat = useMemo(
    () => (live.state.kind === "ready" ? liveView(live.state.data, state.filters).seat : null),
    [live.state, state.filters],
  );

  // The cohorts the bar offers: the institution's on the signed-in host, the
  // sample's on /demo.
  const cohortsOffered: readonly { readonly id: string; readonly name: string }[] | null = session ? cohortList : DEMO_COHORTS;

  // The selected cohort's name, from the selection itself: a cohort whose
  // document is still pending has no payload to read it from, and its
  // building state must still say which cohort it is.
  const selectedCohort = state.cohortId !== null ? (cohortsOffered?.find((c) => c.id === state.cohortId) ?? null) : null;
  const cohortLabel = view.cohortLabel ?? selectedCohort?.name;

  // A cut the page cannot show for a reason that is not the floor (Sep 19
  // 2026, review round 47): held back by the Worker so no student can be
  // singled out, a slice still being prepared on an institution that already
  // has figures, or a read that failed. It gets its own card, hero line and
  // rail reason (panels/CutUnavailable.tsx). It fell through to the first-day
  // screen until now, with the Worker's sentence in the filter note.
  const sliceLabel = MENTORSHIP_FILTERS.find((m) => m.id === state.filters.mentorship)?.label ?? "This group";
  const wholeHasFigures = live.state.kind === "ready" && live.state.data.metrics !== null;
  const refusedCut: RefusedCut | null =
    selection === null
      ? null
      : cutDown && cohortMetrics.kind === "down"
        ? {
            name: state.cohortId !== null ? (selectedCohort?.name ?? "This cohort") : sliceLabel,
            kind: state.cohortId !== null ? "cohort" : "slice",
            state: cohortMetrics.code === "CUT_WITHHELD" ? "held" : "down",
            reason: cohortMetrics.message,
          }
        : state.cohortId === null && cohortReady !== null && cohortReady.metrics === null && wholeHasFigures
          ? { name: sliceLabel, kind: "slice", state: "pending", reason: SLICE_PENDING }
          : null;

  // What the bar says beside the cohort select. A refused cut's reason is its
  // card's, not the note's.
  const cohortNote =
    state.cohortId === null
      ? null
      : cohortMetrics.kind === "loading"
        ? "Loading the cohort's figures."
        : COHORT_SELECTOR_NOTE;

  // Every hook above this line. A signed-in screen shows the gate until its
  // data is here, or the reason it is not coming.
  if (session && live.state.kind !== "ready") {
    return <LiveGate state={live.state} reload={live.reload} />;
  }
  const isLive = live.state.kind === "ready";

  // Under the floor nothing on the scrolling page is a section: the rail
  // dims every one with the reason, and the cohort-building card points at
  // Invitations, its own screen since Sep 17 2026.
  const sections: AdminSectionId[] = view.belowFloor ? [] : [...ADMIN_SECTIONS];
  const basePath = demo ? "/demo" : "/admin";
  // The sample scenario travels to Invitations, as it does on the rail.
  const invitationsHref = `${basePath}/invitations${
    state.scenario !== DEFAULT_SCENARIO ? `?scenario=${state.scenario}` : ""
  }`;

  const prevAvailable = view.comparisonAvailable.prev.ok;

  // The window, the student filters and the demonstration cohort act on the
  // sample's student list. A live snapshot is one whole-population read, so
  // those commands are not offered to a signed-in reader.
  const sampleCommands: PaletteCommand[] = isLive ? [] : [
    // Keywords are per option, not shared across the group: a search for
    // "waiting" that returns all three mentorship filters has told the reader
    // nothing, and a palette that cannot narrow is a menu with extra steps.
    ...MENTORSHIP_FILTERS.map((m) => ({
      id: `mentorship-${m.id}`,
      group: "Filter students",
      label: m.label,
      selected: m.id === view.filters.mentorship,
      keywords:
        m.id === "matched"
          ? "matched mentor paired"
          : m.id === "waiting"
            ? "waiting unmatched no mentor queue"
            : "everyone all clear",
      run: () => setFilters({ mentorship: m.id }),
    })),
    {
      id: "clear-filters",
      group: "Filter students",
      label: "Clear every filter",
      keywords: "reset default",
      run: () => update((current) => ({ ...current, filters: DEFAULT_FILTERS })),
    },
    ...SCENARIOS.map((s) => ({
      id: `scenario-${s.id}`,
      group: "Sample scenario",
      label: `${s.label}, ${s.caption}`,
      hint: s.id === state.scenario ? "Current" : undefined,
      selected: s.id === state.scenario,
      keywords: "sample demo state empty",
      run: () => update({ scenario: s.id }, true),
    })),
  ];

  // The institution's cohorts (Sep 17 2026), by name, and the way back. On
  // both hosts, with the sample's own on /demo.
  const pickCohort = (cohortId: string | null) =>
    update((current) => ({ ...current, cohortId, filters: { ...current.filters, cohort: cohortId } }), true);
  const cohortCommands: PaletteCommand[] =
    cohortsOffered && cohortsOffered.length > 0
      ? [
          {
            id: "cohort-whole",
            group: "Cohort",
            label: "Whole institution",
            selected: state.cohortId === null,
            keywords: "cohort everyone all clear",
            run: () => pickCohort(null),
          },
          ...cohortsOffered.map((c) => ({
            id: `cohort-${c.id}`,
            group: "Cohort",
            label: c.name,
            hint: c.id === state.cohortId ? "Current" : undefined,
            selected: c.id === state.cohortId,
            keywords: "cohort department semester program group",
            run: () => pickCohort(c.id),
          })),
        ]
      : [];

  const commands: PaletteCommand[] = [
    ...cohortCommands,
    ...COMPARISONS.map((c) => ({
      id: `compare-${c.id}`,
      group: "Compare with",
      label: c.label,
      hint:
        c.id === "prev" && !prevAvailable
          ? "Not available in your first term"
          : c.id === view.filters.compare
            ? "Current"
            : undefined,
      selected: c.id === view.filters.compare,
      keywords: "comparison benchmark against",
      run: () => {
        if (c.id === "prev" && !prevAvailable) return;
        setFilters({ compare: c.id });
      },
    })),
    ...sampleCommands,
    /* No join commands while the code is not issued (Sep 14 2026): a tenant a
       card checkout created has none until P3 connects it, and these would
       copy a dead link and an empty string. */
    ...(view.institution.joinCode === "" ? [] : [
    {
      id: "copy-link",
      group: "Claiming",
      label: "Copy the join link",
      hint: view.institution.joinUrl,
      keywords: "share students invite url",
      run: () => {
        void navigator.clipboard
          ?.writeText(`https://${view.institution.joinUrl}`)
          .catch(() => undefined);
      },
    },
    {
      id: "copy-code",
      group: "Claiming",
      label: "Copy the join code",
      hint: view.institution.joinCode,
      keywords: "share students invite code",
      run: () => {
        void navigator.clipboard
          ?.writeText(view.institution.joinCode)
          .catch(() => undefined);
      },
    },
    ]),
    {
      id: "copy-view",
      group: "Share",
      label: "Copy a link to this exact view",
      hint: "Filters travel with it",
      keywords: "url deep link share state",
      run: () => {
        void navigator.clipboard
          ?.writeText(window.location.href)
          .catch(() => undefined);
      },
    },
    {
      id: "export-csv",
      group: "Share",
      label: "Export this view as a CSV",
      hint: "Aggregates only",
      keywords: "download data spreadsheet",
      run: () => downloadCsv(view),
    },
    {
      id: "print",
      group: "Share",
      label: "Export as PDF, or print",
      keywords: "board pdf paper export print",
      run: () => window.print(),
    },
  ];

  return (
    <AdminShell
      institution={view.institution}
      viewer={viewerOf(session)}
      basePath={basePath}
      page="dashboard"
      isSample={!isLive}
      sections={sections}
      dimmedReason={refusedCut ? (label) => refusedRailReason(refusedCut, label) : undefined}
      seat={wholeSeat ?? view.seat}
      headerActions={<ExportPdfButton />}
      commands={commands}
    >
      <Hero view={view} cohortLabel={cohortLabel} refused={refusedCut} />

      <FilterBar
        state={state}
        view={view}
        live={isLive}
        cohorts={cohortsOffered}
        cohortNote={cohortNote}
        update={update}
        setFilters={setFilters}
      />

      {/* The sample notice left every screen on Sep 17 2026 (Thomas), and on
          Sep 18 the way out to pricing moved from under the filters to the
          top bar, where the shell puts it on every sample screen. */}

      {refusedCut ? (
        <CutUnavailable
          cut={refusedCut}
          legalName={view.institution.legalName}
          onShowAll={() => (refusedCut.kind === "cohort" ? pickCohort(null) : setFilters({ mentorship: "any" }))}
          enrollmentHref={refusedCut.kind === "cohort" ? `${basePath}/enrollment` : undefined}
        />
      ) : view.belowFloor ? (
        <CohortBuilding
          view={view}
          invitationsHref={cohortLabel ? undefined : invitationsHref}
          cohort={cohortLabel ? { name: cohortLabel, enrollmentHref: `${basePath}/enrollment` } : undefined}
        />
      ) : (
        <>
          {/* Page order is rail order (ADMIN_SECTIONS): Overview, then the
              Activity group, Mentee Goals last in it (Thomas, Sep 17 2026).
              Invitations is its own screen since the same day. */}
          <OverviewPanel view={view} />
          <MentorshipPanel view={view} />
          <ActivityPanel view={view} />
          <MilestonesPanel view={view} />
          <NeedsPanel view={view} />
        </>
      )}
    </AdminShell>
  );
}
